import type { EngineeringChartSpec } from "./chart-specs";

// Meeting Mode deck resolution. The stored state is an ordered list of slides
// (each 1–3 chart spec ids) plus a "Not presented" tray (excludedIds). Cards
// in neither place — i.e. specs added to ENGINEERING_CHART_SPECS after the
// arrangement was saved — auto-append as their own full-screen slide, so new
// charts still join the deck automatically (the old deny-list spirit).
// Legacy settings documents predate `slides` (null): they resolve to one card
// per slide in registry order minus exclusions, so the v1 document keeps
// working unchanged.
// Like chart-specs, this module is intentionally dependency-free so root
// vitest tests can import it directly.

export interface PresentationSettings {
	excludedIds: string[];
	/** Ordered slides of chart spec ids; null on legacy (pre-arrangement) docs. */
	slides: string[][] | null;
	updatedAt: string | null;
	updatedBy: string | null;
}

export const MAX_CARDS_PER_SLIDE = 3;

/** Layout is derived from card count, never stored. */
export type SlideLayout = "full" | "side-by-side" | "stacked-left";

export const slideLayout = (cardCount: number): SlideLayout =>
	cardCount >= 3 ? "stacked-left" : cardCount === 2 ? "side-by-side" : "full";

export const SLIDE_LAYOUT_LABELS: Record<SlideLayout, string> = {
	full: "Full screen",
	"side-by-side": "Side by side",
	"stacked-left": "2 left + 1 right"
};

export interface DeckArrangement {
	slides: string[][];
	excludedIds: string[];
}

/**
 * Canonicalize an arrangement against the live registry. Invariants of the
 * result (also the shape persisted on save):
 * - ids the registry no longer knows are dropped everywhere;
 * - a card appears exactly once: first slide placement wins, and a card that
 *   is both placed and excluded stays placed (deterministic under concurrent
 *   edits);
 * - slides hold at most MAX_CARDS_PER_SLIDE cards; emptied slides prune away;
 * - specs in neither a slide nor the tray auto-append as their own
 *   full-screen slide in registry order;
 * - excludedIds is normalized to registry order;
 * - legacy input (slides === null) resolves to one card per slide in registry
 *   order minus exclusions.
 * Idempotent: resolving an already-canonical arrangement is a no-op.
 */
export const resolveArrangement = (
	specs: readonly EngineeringChartSpec[],
	settings: Pick<PresentationSettings, "slides" | "excludedIds">
): DeckArrangement => {
	const known = new Set(specs.map((spec) => spec.id));
	const excludedInput = new Set(
		settings.excludedIds.filter((id) => known.has(id))
	);

	if (settings.slides === null) {
		return {
			slides: specs
				.filter((spec) => !excludedInput.has(spec.id))
				.map((spec) => [spec.id]),
			excludedIds: specs
				.map((spec) => spec.id)
				.filter((id) => excludedInput.has(id))
		};
	}

	const placed = new Set<string>();
	const slides: string[][] = [];
	for (const slide of settings.slides) {
		const cardIds: string[] = [];
		for (const id of slide) {
			if (!known.has(id) || placed.has(id)) {
				continue;
			}
			if (cardIds.length >= MAX_CARDS_PER_SLIDE) {
				break;
			}
			placed.add(id);
			cardIds.push(id);
		}
		if (cardIds.length > 0) {
			slides.push(cardIds);
		}
	}

	for (const spec of specs) {
		if (!placed.has(spec.id) && !excludedInput.has(spec.id)) {
			slides.push([spec.id]);
		}
	}

	return {
		slides,
		excludedIds: specs
			.map((spec) => spec.id)
			.filter((id) => excludedInput.has(id) && !placed.has(id))
	};
};

/** Deck = canonical slides mapped to their specs (ids are pre-validated). */
export const resolveDeck = (
	specs: readonly EngineeringChartSpec[],
	slides: readonly (readonly string[])[]
): EngineeringChartSpec[][] => {
	const byId = new Map(specs.map((spec) => [spec.id, spec]));
	return slides.map((slide) =>
		slide.flatMap((id) => {
			const spec = byId.get(id);
			return spec ? [spec] : [];
		})
	);
};

export const arrangementsEqual = (
	a: DeckArrangement,
	b: DeckArrangement
): boolean =>
	a.slides.length === b.slides.length &&
	a.slides.every(
		(slide, index) =>
			slide.length === b.slides[index].length &&
			slide.every((id, cardIndex) => id === b.slides[index][cardIndex])
	) &&
	a.excludedIds.length === b.excludedIds.length &&
	a.excludedIds.every((id, index) => id === b.excludedIds[index]);
